import { escapeRegExp, kebabCase, snakeCase, isEmpty, unionBy, union } from 'lodash-es';
import { ref } from 'vue';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { joinPaths, queryToObject } from '~/lib/utils/url_utility';
import AccessorUtilities from '~/lib/utils/accessor';
import { parseBoolean } from '~/lib/utils/common_utils';
import { getDraft, updateDraft } from '~/lib/utils/autosave';
import { TYPE_EPIC, TYPE_ISSUE } from '~/issues/constants';
import Tracking from '~/tracking';

import {
  DEFAULT_PAGE_SIZE_CHILD_ITEMS,
  DETAIL_VIEW_QUERY_PARAM_NAME,
  NAME_TO_ENUM_MAP,
  NEW_WORK_ITEM_GID,
  STATE_CLOSED,
  WIDGET_TYPE_ASSIGNEES,
  WIDGET_TYPE_AWARD_EMOJI,
  WIDGET_TYPE_COLOR,
  WIDGET_TYPE_CRM_CONTACTS,
  WIDGET_TYPE_CURRENT_USER_TODOS,
  WIDGET_TYPE_CUSTOM_FIELDS,
  WIDGET_TYPE_DESCRIPTION,
  WIDGET_TYPE_DESIGNS,
  WIDGET_TYPE_DEVELOPMENT,
  WIDGET_TYPE_EMAIL_PARTICIPANTS,
  WIDGET_TYPE_ERROR_TRACKING,
  WIDGET_TYPE_HEALTH_STATUS,
  WIDGET_TYPE_HIERARCHY,
  WIDGET_TYPE_ITERATION,
  WIDGET_TYPE_LABELS,
  WIDGET_TYPE_LINKED_ITEMS,
  WIDGET_TYPE_LINKED_RESOURCES,
  WIDGET_TYPE_MILESTONE,
  WIDGET_TYPE_NOTIFICATIONS,
  WIDGET_TYPE_NOTES,
  WIDGET_TYPE_PARTICIPANTS,
  WIDGET_TYPE_PROGRESS,
  WIDGET_TYPE_START_AND_DUE_DATE,
  WIDGET_TYPE_STATUS,
  WIDGET_TYPE_TIME_TRACKING,
  WIDGET_TYPE_VULNERABILITIES,
  WIDGET_TYPE_WEIGHT,
  WIDGET_TYPE_BY_FEATURE_KEY,
} from './constants';
import {
  CLOSED_AT_ASC,
  CLOSED_AT_DESC,
  CREATED_ASC,
  CREATED_DESC,
  DUE_DATE_ASC,
  DUE_DATE_DESC,
  MILESTONE_DUE_ASC,
  MILESTONE_DUE_DESC,
  POPULARITY_ASC,
  POPULARITY_DESC,
  START_DATE_ASC,
  START_DATE_DESC,
  TITLE_ASC,
  TITLE_DESC,
  UPDATED_ASC,
  UPDATED_DESC,
} from './list/constants';

export const isAssigneesWidget = (widget) => widget.type === WIDGET_TYPE_ASSIGNEES;

export const isMilestoneWidget = (widget) => widget.type === WIDGET_TYPE_MILESTONE;

export const isNotesWidget = (widget) => widget.type === WIDGET_TYPE_NOTES;

export const isStatusWidget = (widget) => widget.type === WIDGET_TYPE_STATUS;

export const findAssigneesWidget = (workItem) =>
  workItem?.features?.assignees ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_ASSIGNEES);

export const findAwardEmojiWidget = (workItem) =>
  workItem?.features?.awardEmoji ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_AWARD_EMOJI);

export const findColorWidget = (workItem) =>
  workItem?.features?.color ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_COLOR);

export const findCrmContactsWidget = (workItem) =>
  workItem?.features?.crmContacts ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_CRM_CONTACTS);

export const findCurrentUserTodosWidget = (workItem) =>
  workItem?.features?.currentUserTodos ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_CURRENT_USER_TODOS);

export const findCustomFieldsWidget = (workItem) =>
  workItem?.features?.customFields ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_CUSTOM_FIELDS);

export const findDescriptionWidget = (workItem) =>
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_DESCRIPTION);

export const findDesignsWidget = (workItem) =>
  workItem?.features?.designs ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_DESIGNS);

export const findDevelopmentWidget = (workItem) =>
  workItem?.features?.development ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_DEVELOPMENT);

export const findEmailParticipantsWidget = (workItem) =>
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_EMAIL_PARTICIPANTS);

export const findErrorTrackingWidget = (workItem) =>
  workItem?.features?.errorTracking ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_ERROR_TRACKING);

export const findHealthStatusWidget = (workItem) =>
  workItem?.features?.healthStatus ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_HEALTH_STATUS);

export const findHierarchyWidget = (workItem) =>
  workItem?.features?.hierarchy ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_HIERARCHY);

export const findIterationWidget = (workItem) =>
  workItem?.features?.iteration ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_ITERATION);

export const findLabelsWidget = (workItem) =>
  workItem?.features?.labels ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_LABELS);

export const findLinkedItemsWidget = (workItem) =>
  workItem?.features?.linkedItems ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_LINKED_ITEMS);

export const findBlockerLinkedItems = (workItem) =>
  workItem?.features?.linkedItems?.linkedItems?.nodes ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_LINKED_ITEMS)?.linkedItems?.nodes;

export const findOpenChildItemsCountsByType = (workItem) =>
  workItem?.features?.hierarchy?.rolledUpCountsByType ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_HIERARCHY)?.rolledUpCountsByType;

export const findLinkedResourcesWidget = (workItem) =>
  workItem?.features?.linkedResources ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_LINKED_RESOURCES);

export const findMilestoneWidget = (workItem) =>
  workItem?.features?.milestone ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_MILESTONE);

export const findNotificationsWidget = (workItem) =>
  workItem?.features?.notifications ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_NOTIFICATIONS);

export const findNotesWidget = (workItem) =>
  workItem?.features?.notes ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_NOTES);

export const findParticipantsWidget = (workItem) =>
  workItem?.features?.participants ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_PARTICIPANTS);

export const findProgressWidget = (workItem) =>
  workItem?.features?.progress ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_PROGRESS);

export const findStartAndDueDateWidget = (workItem) =>
  workItem?.features?.startAndDueDate ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_START_AND_DUE_DATE);

export const findStatusWidget = (workItem) =>
  workItem?.features?.status ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_STATUS);

export const findTimeTrackingWidget = (workItem) =>
  workItem?.features?.timeTracking ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_TIME_TRACKING);

export const findVulnerabilitiesWidget = (workItem) =>
  workItem?.features?.vulnerabilities ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_VULNERABILITIES);

export const findWeightWidget = (workItem) =>
  workItem?.features?.weight ||
  workItem?.widgets?.find((widget) => widget.type === WIDGET_TYPE_WEIGHT);

// Builds a widget-type-keyed metadata map from a work item, overlaying migrated `features`
// over the `widgets[]`-derived map (falls back to `widgets[]` when `features` is absent).
export const getMetadataWidgetsFromWorkItem = (workItem) => {
  const metadataWidgets =
    workItem?.widgets?.reduce((acc, widget) => {
      if (widget.type) {
        acc[widget.type] = widget;
      }
      return acc;
    }, {}) || {};

  const { features } = workItem || {};
  if (!features) {
    return metadataWidgets;
  }

  return Object.entries(WIDGET_TYPE_BY_FEATURE_KEY).reduce((acc, [featureKey, widgetType]) => {
    if (!isEmpty(features[featureKey])) {
      acc[widgetType] = { type: widgetType, ...features[featureKey] };
    }
    return acc;
  }, metadataWidgets);
};

export const findHierarchyWidgetChildren = (workItem) =>
  findHierarchyWidget(workItem)?.children?.nodes || [];

export const findHierarchyWidgetAncestors = (workItem) =>
  findHierarchyWidget(workItem)?.ancestors?.nodes || [];

export const formatLabelForListbox = (label) => ({
  text: label.title,
  value: label.id,
  color: label.color,
});

export const formatUserForListbox = (user) => ({
  ...user,
  text: user.name,
  value: user.id,
});

export const convertTypeEnumToName = (workItemTypeEnum) =>
  Object.keys(NAME_TO_ENUM_MAP).find((name) => NAME_TO_ENUM_MAP[name] === workItemTypeEnum);

// Type names are stored in title case, so mid-sentence interpolation needs sentence case.
// https://design.gitlab.com/content/ui-text#capitalization
export const lowercaseWorkItemType = (workItemType) => workItemType?.toLowerCase() ?? '';

/**
 * TODO: Remove this method with https://gitlab.com/gitlab-org/gitlab/-/issues/479637
 * We're currently setting children count per page based on `DEFAULT_PAGE_SIZE_CHILD_ITEMS`
 * but we need to find an ideal page size that's usable and fast enough. In order to test
 * correct page size in production with actual data, this method allows us to set page
 * size using query param (while falling back to `DEFAULT_PAGE_SIZE_CHILD_ITEMS`).
 */
export const getDefaultHierarchyChildrenCount = () => {
  const { children_count } = queryToObject(window.location.search);
  return Number(children_count) || DEFAULT_PAGE_SIZE_CHILD_ITEMS;
};

/**
 * Returns the panel the current URL asks for, or `undefined` when it asks for none.
 * A panel key doubles as its `?show=` value, so callers compare the result to their own key.
 */
export const getRequestedPanel = () =>
  queryToObject(window.location.search)?.[DETAIL_VIEW_QUERY_PARAM_NAME];

export const formatAncestors = (workItem) =>
  findHierarchyWidgetAncestors(workItem).map((ancestor) => ({
    ...ancestor,
    icon: ancestor.workItemType?.iconName,
    href: ancestor.webUrl,
  }));

export const findHierarchyWidgetDefinition = (workItem) =>
  workItem.workItemType.widgetDefinitions?.find(
    (widgetDefinition) => widgetDefinition.type === WIDGET_TYPE_HIERARCHY,
  );

export const getParentGroupName = (namespaceFullName) => {
  const parts = namespaceFullName.split('/');
  // Gets the second-to-last item in the reference path
  return parts.length > 1 ? parts[parts.length - 2].trim() : '';
};

export const autocompleteDataSources = (autocompleteSourcesPaths = {}) => {
  const sources = {
    ...autocompleteSourcesPaths,
    statuses: true, // Include `statuses` as a property so GLFM autocompletion is enabled
  };

  // TODO - remove in %18.5. Adding this temporarily for multi-version compatibility
  if (autocompleteSourcesPaths.merge_requests) {
    sources.mergeRequests = autocompleteSourcesPaths.merge_requests;
  }

  return sources;
};

/**
 * Constructs the path for creating a new work item
 */
export const newWorkItemPath = ({ fullPath, isGroup = false, query = '' }) => {
  const domain = gon.relative_url_root || '';
  const basePath = isGroup ? `groups/${fullPath}` : fullPath;
  return `${domain}/${basePath}/-/work_items/new${query}`;
};

export const getDisplayReference = (workItemFullPath, workitemReference) => {
  // The full reference is replaced by IID reference in case the project and group are same.
  // e.g., gitlab-org/gitlab-test#45 will be shown as #45
  if (workitemReference.startsWith(`${workItemFullPath}#`)) {
    return workitemReference.replace(workItemFullPath, '');
  }
  return workitemReference;
};

export const isReference = (input) => {
  /**
   * The regular expression checks if the `value` is
   * a project work item or group work item.
   * e.g., gitlab-org/project-path#101 or gitlab-org&101
   * or #1234
   */

  return /^([\w-]+(?:\/[\w-]+)*)?[#&](\d+)$/.test(input);
};

export const sortNameAlphabetically = (a, b) => {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
};

/**
 * Builds path to Roadmap page pre-filtered by
 * work item iid
 *
 * @param {string} fullPath the path to the group
 * @param {string} iid the iid of the work item
 */
export const workItemRoadmapPath = (fullPath, iid) => {
  const domain = gon.relative_url_root || '';
  // We're hard-coding the values of `layout` & `timeframe_range_type` as those exist in `ee/app/assets/javascripts/roadmap/constants.js`
  // and importing those here also requires a corresponding file in non-EE scope and that's overengineering a query param.
  // This won't be needed once https://gitlab.com/gitlab-org/gitlab/-/issues/353191 is resolved.
  return `${domain}/groups/${fullPath}/-/roadmap?epic_iid=${iid}&layout=MONTHS&timeframe_range_type=CURRENT_YEAR`;
};

/**
 * Builds unique path for new work item
 *
 * @param {string} fullPath the path to the namespace
 * @param {string} workItemType the type of work item
 */

export const newWorkItemFullPath = (fullPath, workItemType) => {
  if (!workItemType) return undefined;

  const workItemTypeLowercase = workItemType.split(' ').join('-').toLowerCase();
  // eslint-disable-next-line @gitlab/require-i18n-strings
  return `${fullPath}-${workItemTypeLowercase}-id`;
};

export const newWorkItemId = (workItemType) => {
  if (!workItemType) return undefined;

  const workItemTypeLowercase = workItemType.split(' ').join('-').toLowerCase();
  return `${NEW_WORK_ITEM_GID}-${workItemTypeLowercase}`;
};

export const saveToggleToLocalStorage = (key, value) => {
  if (AccessorUtilities.canUseLocalStorage()) {
    localStorage.setItem(key, value);
  }
};

export const getToggleFromLocalStorage = (key, defaultValue = true) => {
  if (AccessorUtilities.canUseLocalStorage()) {
    return parseBoolean(localStorage.getItem(key) ?? defaultValue);
  }
  return null;
};

/**
 * @param {{fullPath?: string, referencePath?: string}} activeItem
 * @param {string} fullPath
 * @param {string} issuableType
 * @returns {string}
 */
export const makeDetailPanelItemFullPath = (activeItem, fullPath, issuableType = TYPE_ISSUE) => {
  if (activeItem?.fullPath) {
    return activeItem.fullPath;
  }
  if (activeItem?.namespace?.fullPath) {
    return activeItem.namespace.fullPath;
  }

  const delimiter = issuableType === TYPE_EPIC ? '&' : '#';
  if (!activeItem?.referencePath) {
    return fullPath;
  }
  return activeItem.referencePath.split(delimiter)[0];
};

/**
 * since legacy epics don't have GID matching the work item ID, we need additional parameters
 * @param {{iid: string, id: string}} activeItem
 * @param {string} fullPath
 * @param {string} issuableType
 * @returns {{iid: string, full_path: string, id: number}}
 */
export const makeDetailPanelUrlParam = (activeItem, fullPath, issuableType = TYPE_ISSUE) => {
  return btoa(
    JSON.stringify({
      iid: activeItem.iid,
      full_path: makeDetailPanelItemFullPath(activeItem, fullPath, issuableType),
      id: getIdFromGraphQLId(activeItem.id),
    }),
  );
};

/**
 * Decodes the `show` detail panel query param and finds the matching item in `items` by id.
 * Returns a null `item` when there's no id, the item is already active, or it isn't in `items`
 * (callers can use `notFound` to tell the last case apart from the other two, e.g. to clean up
 * a stale param).
 * @param {string} queryParam - the raw (still base64-encoded) `show` query param value
 * @param {Object[]} items
 * @param {{id?: string}} activeItem
 * @returns {{item: Object|null, notFound: boolean}}
 */
export const findDetailPanelWorkItem = (queryParam, items, activeItem) => {
  let parsed;
  try {
    parsed = JSON.parse(atob(queryParam));
  } catch {
    // The `show` param is shared with non-work-item panels (e.g. the workplan
    // sentinel), whose values aren't base64-encoded work items. Ignore those.
    return { item: null, notFound: false };
  }
  const { id, full_path: fullPath } = parsed;

  if (!id || getIdFromGraphQLId(activeItem?.id) === id) {
    return { item: null, notFound: false };
  }

  const item = items.find((i) => getIdFromGraphQLId(i.id) === id);
  return item ? { item: { ...item, fullPath }, notFound: false } : { item: null, notFound: true };
};

export const getWorkItemsConnection = (data, useRestApi) =>
  useRestApi ? data?.restWorkItems : data?.namespace?.workItems;

export const getAutosaveKeyQueryParamString = () => {
  const allowedKeysInQueryParamString = [
    'vulnerability_id',
    'discussion_to_resolve',
    'issue[issue_type]',
    'issuable_template',
  ];
  const queryParams = new URLSearchParams(window.location.search);
  // Remove extra params from queryParams
  const allKeys = Array.from(queryParams.keys());
  for (const key of allKeys) {
    if (!allowedKeysInQueryParamString.includes(key)) {
      queryParams.delete(key);
    }
  }

  return queryParams.toString();
};

const getBaseNewWorkItemAutoSaveKey = ({ fullPath, context, relatedItemId }) => {
  const relatedId = getIdFromGraphQLId(relatedItemId);
  const queryParamString = getAutosaveKeyQueryParamString();

  let baseKey = `new-${fullPath}-${context}`;

  if (relatedId) {
    baseKey += `-related-id-${relatedId}`;
  }
  if (queryParamString) {
    baseKey += `-${queryParamString}`;
  }

  return baseKey;
};

export const getNewWorkItemAutoSaveKey = ({ fullPath, context, workItemType, relatedItemId }) => {
  if (!(fullPath && context && workItemType)) {
    throw new Error('Must provide fullPath && context && workItemType');
  }

  const baseKey = getBaseNewWorkItemAutoSaveKey({ fullPath, context, workItemType, relatedItemId });
  return `${baseKey}-${kebabCase(workItemType)}-draft`; // eslint-disable-line @gitlab/require-i18n-strings
};

export const getNewWorkItemWidgetsAutoSaveKey = ({ fullPath, context, relatedItemId }) => {
  if (!(fullPath && context)) {
    throw new Error('Must provide fullPath && context');
  }

  const baseKey = getBaseNewWorkItemAutoSaveKey({ fullPath, context, relatedItemId });
  return `${baseKey}-widgets-draft`;
};

export const getWorkItemFeatures = (draftData) => {
  if (!draftData?.namespace?.workItem?.features) return {};

  const workItemFeatures = draftData.namespace.workItem.features;
  const features = Object.keys(workItemFeatures).reduce((acc, featureName) => {
    return { ...acc, [snakeCase(featureName).toUpperCase()]: workItemFeatures[featureName] };
  }, {});

  return {
    ...features,
    TITLE: draftData.namespace.workItem.title,
    TYPE: draftData.namespace.workItem.workItemType,
  };
};

export const getWorkItemWidgets = (draftData) => {
  if (!draftData?.namespace?.workItem) return {};

  const widgets = {};
  for (const widget of draftData.namespace.workItem.widgets || []) {
    if (widget.type) {
      widgets[widget.type] = widget;
    }
  }
  widgets.TITLE = draftData.namespace.workItem.title;
  widgets.TYPE = draftData.namespace.workItem.workItemType;

  return widgets;
};

export const updateDraftWorkItemType = ({ fullPath, context, workItemType, relatedItemId }) => {
  const widgetsAutosaveKey = getNewWorkItemWidgetsAutoSaveKey({
    fullPath,
    context,
    relatedItemId,
  });
  const sharedCacheWidgets = JSON.parse(getDraft(widgetsAutosaveKey)) || {};
  sharedCacheWidgets.TYPE = workItemType;
  updateDraft(widgetsAutosaveKey, JSON.stringify(sharedCacheWidgets));
};

export const getDraftWorkItemType = ({ fullPath, context, relatedItemId }) => {
  const widgetsAutosaveKey = getNewWorkItemWidgetsAutoSaveKey({
    fullPath,
    context,
    relatedItemId,
  });
  const sharedCacheWidgets = JSON.parse(getDraft(widgetsAutosaveKey)) || {};
  return sharedCacheWidgets.TYPE;
};

export const isItemDisplayable = (item, showClosed) => {
  return item.state !== STATE_CLOSED || (item.state === STATE_CLOSED && showClosed);
};

export const getItems = (showClosed) => {
  return (children) => {
    return children.filter((item) => isItemDisplayable(item, showClosed));
  };
};

export const canRouterNav = ({ fullPath, webUrl, isGroup, issueAsWorkItem }) => {
  const escapedFullPath = escapeRegExp(fullPath);
  // eslint-disable-next-line no-useless-escape
  const groupRegex = new RegExp(`groups\/${escapedFullPath}\/-\/(work_items|epics)\/\\d+`);
  const projectRegex = new RegExp(`${escapedFullPath}/-/(work_items|issues)(/\\d+)?/?$`);
  const canGroupNavigate = groupRegex.test(webUrl) && isGroup;
  const canProjectNavigate = projectRegex.test(webUrl) && issueAsWorkItem;
  return canGroupNavigate || canProjectNavigate;
};

export const createBranchMRApiPathHelper = {
  canCreateBranch({ fullPath, workItemIid }) {
    return joinPaths(
      gon.relative_url_root || '',
      `/${fullPath}/-/issues/${workItemIid}/can_create_branch`,
    );
  },
  createBranch(fullPath) {
    return joinPaths(gon.relative_url_root || '', `/${fullPath}/-/branches`);
  },
  createMR({ fullPath, workItemIid, sourceBranch, targetBranch }) {
    let url = joinPaths(
      gon.relative_url_root || '',
      `/${fullPath}/-/merge_requests/new?merge_request%5Bissue_iid%5D=${workItemIid}&merge_request%5Bsource_branch%5D=${encodeURIComponent(sourceBranch)}`,
    );
    if (targetBranch) {
      url += `&merge_request%5Btarget_branch%5D=${encodeURIComponent(targetBranch)}`;
    }
    return url;
  },
  getRefs({ fullPath }) {
    return joinPaths(gon.relative_url_root || '', `/${fullPath}/refs?search=`);
  },
};

export const formatSelectOptionForCustomField = ({ id, value }) => ({
  text: value,
  value: id,
});

/**
 * This function takes the `descriptionHtml` property of a work item and updates any `<details>`
 * elements within it with an `open=true` attribute to match the current state in the DOM.
 *
 * This is necessary for scenarios such as toggling a checkbox with an opened `<details>` element,
 * which causes the `<details>` element to close when the frontend receives the backend response.
 *
 * @param {HTMLElement} element DOM element containing <details> elements
 * @param {string} descriptionHtml The incoming HTML description
 * @returns {string|null} The updated HTML for the incoming description that preserves the state of the <details> elements
 */
export const preserveDetailsState = (element, descriptionHtml) => {
  const previousDetails = Array.from(element.getElementsByTagName('details'));
  if (!previousDetails.some((details) => details.open)) {
    return null;
  }

  const nextTemplate = document.createElement('div');
  nextTemplate.innerHTML = descriptionHtml; // eslint-disable-line no-unsanitized/property
  const nextDetails = nextTemplate.getElementsByTagName('details');
  if (previousDetails.length !== nextDetails.length) {
    return null;
  }

  Array.from(nextDetails).forEach((details, i) => {
    if (previousDetails[i].open) {
      details.setAttribute('open', 'true');
    }
  });
  return nextTemplate.innerHTML;
};

export const activeWorkItemIds = ref([]);

export const getWorkItemTypeAllowedStatusMap = (workItemTypeNodes) => {
  const workItemTypeAllowedStatusMap = {};

  workItemTypeNodes.forEach((workItemType) => {
    const statuses = workItemType.widgetDefinitions?.find(isStatusWidget)?.allowedStatuses;
    if (statuses) {
      workItemTypeAllowedStatusMap[workItemType.id] = statuses;
    }
  });

  return workItemTypeAllowedStatusMap;
};

/**
 * Unified method for sending tracking events for work item CRUD component collapse/expand toggles
 * @param {string} action
 */
export function trackCrudCollapse(action) {
  // eslint-disable-next-line @gitlab/require-i18n-strings
  const category = 'Work item widget collapse';

  Tracking.event(category, action);
}

const LAST_USED_WORK_ITEM_TYPE_PREFIX = 'freq-wi-type';

export function setLastUsedWorkItemTypeIdForNamespace(workItemTypeId, namespaceFullPath) {
  if (AccessorUtilities.canUseLocalStorage()) {
    const storageKey = `${LAST_USED_WORK_ITEM_TYPE_PREFIX}:${namespaceFullPath}`;
    localStorage.setItem(storageKey, workItemTypeId.toString());
  }
}

export function getLastUsedWorkItemTypeIdForNamespace(namespaceFullPath) {
  if (AccessorUtilities.canUseLocalStorage()) {
    const storageKey = `${LAST_USED_WORK_ITEM_TYPE_PREFIX}:${namespaceFullPath}`;
    return localStorage.getItem(storageKey);
  }
  return null;
}

const combineWidgets = (fullItem, slimItem) => {
  const combinedWidgets = unionBy(fullItem.widgets, slimItem?.widgets, 'type');

  return {
    widgets: combinedWidgets.reduce((acc, widget) => {
      const slimWidget = slimItem?.widgets.find((w) => w.type === widget.type);
      const widgetToUse =
        slimWidget && Object.keys(slimWidget).length > Object.keys(widget).length
          ? slimWidget
          : widget;
      acc.push(widgetToUse);
      return acc;
    }, []),
  };
};

/**
 *
 * @param {Object} fullItem - The WorkItem query that has all the data
 * @param {Object} slimItem - The WorkItem query that is leaner to get a faster response
 * @returns {Object} - For each feature key, we return the version that has the most data inside a single object.
 */
const combineFeatures = (fullItem, slimItem) => {
  const fullItemFeaturesKeys = fullItem?.features ? Object.keys(fullItem.features) : [];
  const slimItemFeaturesKeys = slimItem?.features ? Object.keys(slimItem.features) : [];

  const featuresUnion = union(fullItemFeaturesKeys, slimItemFeaturesKeys);

  // For each feature, check if the full or slim has more data and use that one.
  return {
    features: featuresUnion.reduce((acc, featureName) => {
      const fullItemData = fullItem?.features[featureName] || {};
      const slimItemData = slimItem?.features[featureName] || {};

      const dataToUse =
        Object.keys(slimItemData).length > Object.keys(fullItemData).length
          ? slimItemData
          : fullItemData;
      return { ...acc, [featureName]: dataToUse };
    }, {}),
  };
};

export function combineWorkItemLists(slimList, fullList, workItemFeaturesField = false) {
  if (isEmpty(fullList)) return slimList;

  return fullList.map((fullItem) => {
    const slimVersion = slimList.find((item) => item.id === fullItem.id);
    const combineFeatureFn = workItemFeaturesField ? combineFeatures : combineWidgets;

    return {
      ...fullItem,
      ...combineFeatureFn(fullItem, slimVersion),
    };
  });
}

export const saveHiddenMetadataKeysToLocalStorage = (key, hiddenKeys) => {
  if (AccessorUtilities.canUseLocalStorage()) {
    localStorage.setItem(key, JSON.stringify(hiddenKeys));
  }
};

export const getHiddenMetadataKeysFromLocalStorage = (key, defaultHiddenKeys = []) => {
  if (!AccessorUtilities.canUseLocalStorage()) return defaultHiddenKeys;

  const stored = localStorage.getItem(key);

  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return defaultHiddenKeys;
    }
  }

  return defaultHiddenKeys;
};

export const isCurrentViewWorkItem = () => {
  const page = document.body.dataset.page || '';
  const descriptionWrapper = document.querySelector('.js-issuable-description-wrapper');

  // Early return for Incident and Ticket pages as those are not work items
  if (['ticket', 'incident'].includes(descriptionWrapper?.dataset.issuableType)) return false;

  // Check page type for possible work items
  return [
    // Group Work Items/Epics List View
    'groups:work_items:index',
    'groups:epics:index',

    // Group Issues List/Detail View
    'groups:issues',

    // Group Issues/Epics Board View
    'groups:boards:index',
    'groups:epic_boards:index',

    // Project Work Items/Issues list View
    'projects:work_items:index',
    'projects:issues:index',

    // Project Issues Board View
    'projects:boards:index',

    // Group Work Items/Epics Detail View
    'groups:work_items:show',
    'groups:epics:show',

    // Project Work Items/Issues Detail View
    'projects:work_items:show',
    'projects:issues:show',
  ].includes(page);
};

export const getSortValue = (item, sortKey) => {
  switch (sortKey) {
    case CREATED_ASC:
    case CREATED_DESC:
      return new Date(item.createdAt);
    case UPDATED_ASC:
    case UPDATED_DESC:
      return new Date(item.updatedAt);
    case CLOSED_AT_ASC:
    case CLOSED_AT_DESC:
      return item.closedAt ? new Date(item.closedAt) : null;
    case TITLE_ASC:
    case TITLE_DESC:
      return item.title?.toLowerCase() || '';
    case POPULARITY_ASC:
    case POPULARITY_DESC:
      return findAwardEmojiWidget(item)?.upvotes || null;
    case MILESTONE_DUE_ASC:
    case MILESTONE_DUE_DESC: {
      const dueDate = findMilestoneWidget(item)?.milestone?.dueDate;
      return dueDate ? new Date(dueDate) : null;
    }
    case DUE_DATE_ASC:
    case DUE_DATE_DESC: {
      const dueDate = findStartAndDueDateWidget(item)?.dueDate;
      return dueDate ? new Date(dueDate) : null;
    }
    case START_DATE_ASC:
    case START_DATE_DESC: {
      const startDate = findStartAndDueDateWidget(item)?.startDate;
      return startDate ? new Date(startDate) : null;
    }
    default:
      return null;
  }
};

export function sortWorkItems(workItems, sortKey, resolveWorkItemSortValue) {
  const isDescending = sortKey.endsWith('_DESC');

  return [...workItems].sort((workItem1, workItem2) => {
    const workItem1Value = resolveWorkItemSortValue(workItem1, sortKey);
    const workItem2Value = resolveWorkItemSortValue(workItem2, sortKey);
    // Handle null values - always keep them at the end
    if (workItem1Value === null && workItem2Value === null) return 0;
    if (workItem1Value === null) return 1;
    if (workItem2Value === null) return -1;

    // Compare values
    let comparison = 0;
    if (workItem1Value < workItem2Value) {
      comparison = -1;
    } else if (workItem1Value > workItem2Value) {
      comparison = 1;
    }

    return isDescending ? -comparison : comparison;
  });
}

export function getSortedWorkItems(workItems, sortKey) {
  return sortWorkItems(workItems, sortKey, getSortValue);
}
